/**
 * Google Analytics 4 (GA4) helper — wraps window.gtag for the entire app.
 *
 * Measurement ID is already loaded via the <script> tag in public/index.html,
 * so we just call `window.gtag(...)` here.  Every helper is a no-op when
 * gtag isn't available (dev, ad-blockers, etc.).
 */

import posthog from '../posthog';

const GA_ID = 'G-5ZM19DS2EY';

function gtag(...args) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

/* ──────────────── Page / Screen views ──────────────── */

/**
 * Fire a virtual page_view for SPA navigation.
 * @param {string} pageName  e.g. 'welcome', 'stories', 'story', 'about'
 * @param {object} [extra]   optional extra params (story title, level, etc.)
 */
export function trackPageView(pageName, extra = {}) {
  gtag('event', 'page_view', {
    page_location: `${window.location.origin}/${pageName}`,
    page_path: `/${pageName}`,
    page_title: pageName,
    ...extra,
  });
  // PostHog's Web Analytics dashboard is built entirely on $pageview events - without this,
  // SPA route changes (React Router) never register there, only the one-time script load did.
  posthog?.capture('$pageview', { page_name: pageName, ...extra });
}

/* ──────────────── Level / Series browsing ──────────────── */

export function trackLevelSelected(level) {
  const properties = { level };
  gtag('event', 'select_level', { event_category: 'navigation', ...properties });
}

export function trackSeriesSelected(seriesName, level) {
  const properties = { series_name: seriesName, level };
  gtag('event', 'select_series', { event_category: 'navigation', ...properties });
}

/* ──────────────── Story / Episode opens ──────────────── */

/**
 * User opened a story card (single-episode stories).
 */
export function trackStoryOpened(story) {
  const properties = {
    story_id: story?.id || story?.file || 'unknown',
    story_title: story?.title || 'unknown',
    story_level: story?.level || story?.data?.level || 'unknown',
  };
  gtag('event', 'open_story', { event_category: 'engagement', ...properties });
  posthog?.capture('open_story', properties);
}

/**
 * User opened a specific episode inside a series.
 */
export function trackEpisodeOpened(episode, seriesName) {
  const properties = {
    episode_id: episode?.id || 'unknown',
    episode_title: episode?.title || 'unknown',
    episode_order: episode?.order ?? 0,
    episode_level: episode?.level || 'unknown',
    series_name: seriesName || 'unknown',
  };
  gtag('event', 'open_episode', { event_category: 'engagement', ...properties });
}

/* ──────────────── Reading / Completion ──────────────── */

/**
 * User marked an episode as read.
 */
export function trackMarkAsRead(storyId, storyTitle, level) {
  const properties = { story_id: storyId, story_title: storyTitle || '', level: level || '' };
  gtag('event', 'mark_as_read', { event_category: 'progress', ...properties });
  posthog?.capture('mark_as_read', properties);
}

/**
 * User un-marked an episode (toggled read off).
 */
export function trackUnmarkRead(storyId) {
  gtag('event', 'unmark_read', {
    event_category: 'progress',
    story_id: storyId,
  });
}

/**
 * User finished reading and the completion dialog appeared.
 */
export function trackReadingCompleted(storyId, storyTitle, level, readingTimeMinutes, wordCount) {
  const properties = {
    story_id: storyId || '',
    story_title: storyTitle || '',
    level: level || '',
    reading_time_minutes: readingTimeMinutes,
    word_count: wordCount,
  };
  gtag('event', 'story_completed', { event_category: 'progress', ...properties });
  posthog?.capture('story_completed', properties);
}

export function trackStoryStarted(story) {
  const properties = {
    story_id: story?.file || story?.id || 'unknown',
    story_title: story?.title || 'unknown',
    story_level: story?.level || 'unknown',
  };
  gtag('event', 'story_started', { event_category: 'engagement', ...properties });
  posthog?.capture('story_started', properties);
}

export function trackThirdStoryCompleted(story) {
  gtag('event', 'third_story_completed', {
    event_category: 'milestone',
    story_id: story?.file || story?.id || 'unknown',
    story_title: story?.title || 'unknown',
  });
}

/* ──────────────── Audio / Karaoke ──────────────── */

export function trackAudioPlay(storyId, useTTS) {
  const properties = { story_id: storyId || '', tts: useTTS ? 'yes' : 'no' };
  gtag('event', 'audio_play', { event_category: 'engagement', ...properties });
  posthog?.capture('audio_play', properties);
}

export function trackAudioPause(storyId) {
  gtag('event', 'audio_pause', {
    event_category: 'engagement',
    story_id: storyId || '',
  });
}

export function trackWordClicked(word, storyId) {
  gtag('event', 'word_click', {
    event_category: 'engagement',
    word,
    story_id: storyId || '',
  });
}

/* ──────────────── Episode navigation ──────────────── */

export function trackEpisodeNavigation(direction, fromIndex, toIndex, seriesName) {
  gtag('event', 'episode_navigate', {
    event_category: 'navigation',
    direction,           // 'next' | 'previous'
    from_index: fromIndex,
    to_index: toIndex,
    series_name: seriesName || '',
  });
}

/* ──────────────── Settings toggles ──────────────── */

export function trackSettingToggle(setting, value) {
  gtag('event', 'setting_change', {
    event_category: 'settings',
    setting_name: setting,  // 'pinyin', 'english', 'font_size'
    setting_value: String(value),
  });
}

/* ──────────────── Auth events ──────────────── */

export function trackLogin(method) {
  const properties = { method: method || 'google' };
  gtag('event', 'login', properties);
  posthog?.capture('login', properties);
}

export function trackSignUp(method) {
  const properties = { method: method || 'google' };
  gtag('event', 'signup', properties);
  posthog?.capture('signup', properties);
}

export function trackLogout() {
  gtag('event', 'logout', { event_category: 'auth' });
  posthog?.capture('logout');
}

/* ──────────────── Paywall / Premium ──────────────── */

export function trackPaywallShown(trigger) {
  const properties = { trigger };
  gtag('event', 'paywall_shown', { event_category: 'monetization', ...properties });
  posthog?.capture('paywall_shown', properties);
}

export function trackPremiumView(trigger) {
  gtag('event', 'premium_view', {
    event_category: 'monetization',
    trigger: trigger || 'paywall',
  });
}

export function trackCheckoutStarted(plan) {
  const properties = { plan: plan || 'unknown' };
  gtag('event', 'checkout_started', { event_category: 'monetization', ...properties });
  posthog?.capture('checkout_started', properties);
}

export function trackPurchase(plan) {
  const properties = { plan: plan || 'unknown' };
  gtag('event', 'purchase', { event_category: 'monetization', ...properties });
  posthog?.capture('purchase', properties);
}

export function trackPremiumUpgrade() {
  gtag('event', 'premium_upgrade', {
    event_category: 'monetization',
  });
}

/* ──────────────── Misc engagement ──────────────── */

export function trackQuizStarted(storyId) {
  const properties = { story_id: storyId || '' };
  gtag('event', 'quiz_started', { event_category: 'engagement', ...properties });
  posthog?.capture('quiz_started', properties);
}

export function trackQuizCompleted(storyId, allCorrect) {
  const properties = { story_id: storyId || '', all_correct: allCorrect ? 'yes' : 'no' };
  gtag('event', 'quiz_completed', { event_category: 'engagement', ...properties });
  posthog?.capture('quiz_completed', properties);
}

/* ──────────────── User identity ──────────────── */

/**
 * Set GA4 user_id and custom user properties after auth resolves.
 * Call whenever user state changes (login, premium upgrade, logout).
 */
export function setUserProperties(user) {
  if (!user?.uid) {
    gtag('set', { user_id: undefined });
    return;
  }
  gtag('set', { user_id: user.uid });
  gtag('set', 'user_properties', {
    is_premium: user.premium ? 'yes' : 'no',
  });
}

/* ──────────────── Continue / chain navigation ──────────────── */

/**
 * User tapped "Continue Reading" on the Welcome page.
 */
export function trackContinueReading(story) {
  const properties = {
    story_id: story?.file || '',
    story_title: story?.title || '',
    story_level: story?.level || '',
  };
  gtag('event', 'continue_reading', { event_category: 'engagement', ...properties });
  posthog?.capture('continue_reading', properties);
}

/**
 * User tapped the "Next Story" button inside the reader.
 */
export function trackNextStory(fromFile, toStory) {
  const properties = {
    from_story: fromFile || '',
    to_story_id: toStory?.file || '',
    to_story_title: toStory?.title || '',
    to_story_level: toStory?.level || '',
  };
  gtag('event', 'next_story_clicked', { event_category: 'navigation', ...properties });
  posthog?.capture('next_story_clicked', properties);
}

/* ──────────────── Social / outbound links ──────────────── */

export function trackSocialClick(platform) {
  gtag('event', 'social_link_click', {
    event_category: 'engagement',
    platform,
  });
}

/* ──────────────── Web Vitals ──────────────── */

/**
 * Pass this directly to reportWebVitals() in index.js.
 * Sends CWV (LCP, FID/INP, CLS, FCP, TTFB) to GA4 as events.
 */
export function trackWebVitals({ name, value, id, delta }) {
  gtag('event', name, {
    event_category: 'web_vitals',
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    metric_id: id,
    metric_delta: Math.round(name === 'CLS' ? (delta ?? value) * 1000 : (delta ?? value)),
    non_interaction: true,
  });
}
